import { useEffect, useRef, useState } from 'react';
import { addMonths, cashRoundingAllowed, firstOfMonth, stateRules, todayIn } from '@njpos/core';
import { StatePicker } from '../components/StatePicker';
import { api, loadSession, saveSession } from '../lib/api';
import { ErrorNote, useLoad } from '../components/ui';

export default function Settings() {
  const s = loadSession()!;
  const [store, setStore] = useState<any>(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState<unknown>(null);
  const [staff, setStaff] = useState({ name: '', pin: '' });
  const [newStore, setNewStore] = useState({ name: '', stateCode: 'NJ', njLicenseNo: '', address: '' });
  const cplRef = useRef<HTMLInputElement>(null);
  const ripRef = useRef<HTMLInputElement>(null);

  useEffect(() => { api<any>('/api/me').then((me) => setStore(me.stores.find((x: any) => x.id === s.storeId))); }, [s.storeId]);
  if (!store) return <div className="empty">Loading…</div>;
  const de = store.settings?.dealEngine ?? {};
  const rules = stateRules(store.state_code);
  const set = (patch: any) => setStore({ ...store, ...patch });
  const setDe = (patch: any) => setStore({ ...store, settings: { ...store.settings, dealEngine: { ...de, ...patch } } });

  async function save() {
    setErr(null); setMsg('');
    try {
      await api(`/api/stores/${store.id}`, { method: 'PATCH', body: {
        name: store.name, njLicenseNo: store.nj_license_no, address: store.address, cashRounding: store.cash_rounding,
        ...(store.tax_pct !== undefined && Number.isFinite(Number(store.tax_pct)) ? { taxRatePpm: Math.round(Number(store.tax_pct) * 10000) } : {}),
        defaultMarkupPct: Number(store.settings?.defaultMarkupPct ?? 30),
        dealEngine: {
          costOfCapitalAnnual: Number(de.costOfCapitalAnnual ?? 0.12), storagePerCaseMonthCents: Number(de.storagePerCaseMonthCents ?? 50),
          maxMonthsCover: Number(de.maxMonthsCover ?? 3), cashLimitCents: de.cashLimitCents ? Number(de.cashLimitCents) : null,
        },
      } });
      setMsg('Saved.');
    } catch (e) { setErr(e); }
  }

  async function uploadCpl() {
    const f = cplRef.current?.files?.[0];
    if (!f) return;
    setErr(null); setMsg('');
    const form = new FormData();
    form.append('saleMonth', addMonths(firstOfMonth(todayIn()), 1));
    form.append('file', f, f.name);
    const rip = ripRef.current?.files?.[0];
    if (rip) form.append('ripFile', rip, rip.name);
    try { const r = await api<any>('/api/cpl/upload', { form }); setMsg(`Loaded ${r.lines} price lines (${r.matched} matched to products, ${r.rips} RIP programs).`); }
    catch (e) { setErr(e); }
  }

  return (
    <div className="stack" style={{ maxWidth: 820 }}>
      <div className="page-head"><div className="grow"><h1>Settings</h1></div></div>
      <ErrorNote error={err} />
      {msg && <div className="notice good">{msg}</div>}
      <ReportEmails />
      <ReceiptSettings />
      <div className="panel"><div className="panel-head"><h2>Add another store</h2></div><div className="panel-body stack">
        <p className="muted">Each store has its own stock, register and staff. You can move stock between stores from Transfers.</p>
        <div className="field-row">
          <label>Store name<input value={newStore.name} onChange={(e) => setNewStore({ ...newStore, name: e.target.value })} /></label>
          <StatePicker value={newStore.stateCode} onChange={(stateCode) => setNewStore({ ...newStore, stateCode })} />
        </div>
        <div className="field-row">
          <label>{stateRules(newStore.stateCode).licenseLabel} number<input value={newStore.njLicenseNo} onChange={(e) => setNewStore({ ...newStore, njLicenseNo: e.target.value })} /></label>
          <label>Address<input value={newStore.address} onChange={(e) => setNewStore({ ...newStore, address: e.target.value })} /></label>
        </div>
        <div><button disabled={newStore.name.trim().length < 2} onClick={async () => {
          setErr(null); setMsg('');
          try {
            const r = await api<any>('/api/stores', { body: { name: newStore.name, stateCode: newStore.stateCode, ...(newStore.njLicenseNo ? { njLicenseNo: newStore.njLicenseNo } : {}), ...(newStore.address ? { address: newStore.address } : {}) } });
            saveSession({ ...s, token: r.token, stores: r.stores });
            setNewStore({ name: '', stateCode: newStore.stateCode, njLicenseNo: '', address: '' });
            setMsg(`${r.store.name} added. Use the store menu to switch to it.`);
          } catch (e) { setErr(e); }
        }}>Add store</button></div>
      </div></div>
      <div className="panel"><div className="panel-head"><h2>Store</h2></div><div className="panel-body stack">
        <div className="field-row">
          <label>Store name<input value={store.name} onChange={(e) => set({ name: e.target.value })} /></label>
          <label>{rules.licenseLabel} number<input value={store.nj_license_no ?? ''} onChange={(e) => set({ nj_license_no: e.target.value })} /></label>
        </div>
        <p className="faint">State: {rules.name}. The register and back office follow {rules.name}’s alcohol rules for this store.</p>
        {rules.needsLegalCheck && <div className="notice">{rules.name}’s rules in PourPoint have not been checked by a lawyer yet. Have your own attorney confirm the below-cost, credit and tax rules before relying on them.</div>}
        <div className="field-row">
          <label>Address<input value={store.address ?? ''} onChange={(e) => set({ address: e.target.value })} /></label>
          <label>Sales tax (%)<input inputMode="decimal" value={store.tax_pct ?? String(store.tax_rate_ppm / 10000)} onChange={(e) => set({ tax_pct: e.target.value })} /></label>
        </div>
        {rules.localTaxNote && <p className="faint">{rules.localTaxNote}</p>}
        {rules.cashRounding && (cashRoundingAllowed(rules, todayIn(store.timezone)) ? (
          <label className="row" style={{ display: 'flex', fontWeight: 500, color: 'var(--ink)' }}>
            <input type="checkbox" style={{ width: 'auto', minHeight: 0 }} checked={store.cash_rounding} onChange={(e) => set({ cash_rounding: e.target.checked })} />
            Round cash sales to the nearest 5¢ ({rules.cashRounding.notice})
          </label>
        ) : <p className="faint">Rounding cash sales to the nearest 5¢ becomes legal in {rules.name} on {rules.cashRounding.from}.</p>)}
        <p className="faint">Register sign-in link for this store: <span className="num">{location.origin}/?store={store.id}</span></p>
      </div></div>

      <div className="panel"><div className="panel-head"><h2>Buying rules</h2></div><div className="panel-body stack">
        <p className="muted">How hard the buy list leans into deals. Stores with tight cash or little back-room space should lower months of stock and set a cash limit.</p>
        <div className="field-row">
          <label>Most months of stock to buy<input inputMode="decimal" value={de.maxMonthsCover ?? 3} onChange={(e) => setDe({ maxMonthsCover: e.target.value })} /></label>
          <label>Cost of money (% a year)<input inputMode="decimal" value={Math.round((de.costOfCapitalAnnual ?? 0.12) * 100)} onChange={(e) => setDe({ costOfCapitalAnnual: Number(e.target.value) / 100 })} /></label>
          <label>Storage cost per case per month ($)<input inputMode="decimal" value={((de.storagePerCaseMonthCents ?? 50) / 100).toFixed(2)} onChange={(e) => setDe({ storagePerCaseMonthCents: Math.round(Number(e.target.value) * 100) })} /></label>
          <label>Largest single invoice ($, blank = no limit)<input inputMode="decimal" value={de.cashLimitCents ? (de.cashLimitCents / 100).toFixed(0) : ''} onChange={(e) => setDe({ cashLimitCents: e.target.value ? Math.round(Number(e.target.value) * 100) : null })} /></label>
          <label>Markup for new items from invoices (%)<input inputMode="decimal" value={store.settings?.defaultMarkupPct ?? 30} onChange={(e) => set({ settings: { ...store.settings, defaultMarkupPct: e.target.value } })} /></label>
        </div>
        <div><button className="primary" onClick={save}>Save settings</button></div>
      </div></div>

      <div className="panel"><div className="panel-head"><h2>Register staff</h2></div><div className="panel-body stack-s">
        <div className="field-row">
          <label>Name<input value={staff.name} onChange={(e) => setStaff({ ...staff, name: e.target.value })} /></label>
          <label>PIN (4–6 digits)<input inputMode="numeric" value={staff.pin} onChange={(e) => setStaff({ ...staff, pin: e.target.value.replace(/\D/g, '').slice(0, 6) })} /></label>
        </div>
        <div><button disabled={!staff.name || staff.pin.length < 4} onClick={async () => {
          try { await api('/api/staff', { body: staff }); setMsg(`${staff.name} can now sign in at the register.`); setStaff({ name: '', pin: '' }); } catch (e) { setErr(e); }
        }}>Add cashier</button></div>
      </div></div>

      <div className="panel"><div className="panel-head"><h2>Upload a price list</h2></div><div className="panel-body stack-s">
        <p className="muted">Price lists are loaded for every store each month. If your rep sent one that isn’t showing on the buy list, upload it here (CSV or Excel). It is shared with all stores, like the ABC filing it comes from.</p>
        <div className="field-row">
          <label>Price list file<input ref={cplRef} type="file" accept=".csv,.xlsx" /></label>
          <label>RIP sheet (CSV only, optional)<input ref={ripRef} type="file" accept=".csv" /></label>
        </div>
        <div><button onClick={uploadCpl}>Upload for next month</button></div>
      </div></div>
    </div>
  );
}

// What prints on the customer's receipt: logo, header, return policy and footer.
function ReceiptSettings() {
  const [f, setF] = useState<any>(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => { api<any>('/api/receipt-settings').then(setF).catch(setErr); }, []);
  if (!f) return <ErrorNote error={err} />;
  const set = (k: string, v: any) => setF({ ...f, [k]: v });
  function logo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 150_000) { setErr(new Error('Keep the logo under 150 KB (a small black-and-white image prints best).')); return; }
    const r = new FileReader();
    r.onload = () => set('logo', String(r.result));
    r.readAsDataURL(file);
  }
  return (
    <div className="panel"><div className="panel-head"><h2>Receipt</h2></div><div className="panel-body stack">
      <ErrorNote error={err} />
      {msg && <div className="notice good">{msg}</div>}
      <label>Top text (under your store name)<textarea rows={2} value={f.header ?? ''} onChange={(e) => set('header', e.target.value)} placeholder="Phone number, website, hours" /></label>
      <label>Return policy<textarea rows={2} value={f.policy ?? ''} onChange={(e) => set('policy', e.target.value)} placeholder="e.g. Sealed bottles with receipt within 7 days" /></label>
      <label>Bottom text<textarea rows={2} value={f.footer ?? ''} onChange={(e) => set('footer', e.target.value)} placeholder="e.g. Must be 21 to purchase alcohol." /></label>
      <label className="row" style={{ display: 'flex', fontWeight: 500, color: 'var(--ink)', gap: 6 }}><input type="checkbox" style={{ width: 'auto', minHeight: 0 }} checked={!!f.showLicense} onChange={(e) => set('showLicense', e.target.checked)} /> Print the NJ licence number</label>
      <div className="row wrap"><label>Logo<input type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={logo} /></label>{f.logo && <><img src={f.logo} alt="Logo" style={{ maxHeight: 48 }} /><button onClick={() => set('logo', null)}>Remove logo</button></>}</div>
      <div><button className="primary" onClick={async () => {
        setErr(null); setMsg('');
        try { await api('/api/receipt-settings', { method: 'PUT', body: { header: f.header ?? '', footer: f.footer ?? '', policy: f.policy ?? '', showLicense: !!f.showLicense, logo: f.logo ?? null } }); setMsg('Saved. The next receipt will use it.'); } catch (e) { setErr(e); }
      }}>Save receipt text</button></div>
    </div></div>
  );
}

// Reports that arrive by email on a schedule.
function ReportEmails() {
  const data = useLoad(() => api<{ configured: boolean; reports: { key: string; label: string }[]; schedules: any[] }>('/api/report-emails'), []);
  const [f, setF] = useState({ name: '', report: 'sales_summary', frequency: 'daily', sendHour: '7', recipients: '' });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState<unknown>(null);
  const d = data.data;
  const hourLabel = (h: number) => `${h % 12 || 12}:00 ${h < 12 ? 'am' : 'pm'}`;
  async function run(fn: () => Promise<unknown>, ok = '') { setErr(null); setMsg(''); try { await fn(); if (ok) setMsg(ok); data.reload(); } catch (e) { setErr(e); } }
  return (
    <div className="panel"><div className="panel-head"><h2>Report emails</h2></div><div className="panel-body stack">
      <p className="muted">Get a report in your inbox every day, week or month, sent in your store's own time. Daily reports cover yesterday, weekly ones the last Monday to Sunday, monthly ones the last month.</p>
      <ErrorNote error={err ?? data.error} />
      {msg && <div className="notice good">{msg}</div>}
      {d && !d.configured && <div className="notice warn">Email is not set up on this server yet, so nothing can be sent. The person who runs the server needs to add the mail settings (see the README, "Report emails").</div>}
      {d && (
        <>
          <table className="grid"><thead><tr><th>Name</th><th>Report</th><th>When</th><th>Sent to</th><th>Last</th><th /></tr></thead>
            <tbody>{d.schedules.map((s) => (
              <tr key={s.id} style={s.active ? undefined : { opacity: 0.5 }}>
                <td>{s.name}</td><td>{d.reports.find((r) => r.key === s.report)?.label}</td><td>{s.frequency} at {hourLabel(s.send_hour)}</td><td>{(s.recipients as string[]).join(', ')}</td>
                <td className="faint">{s.last_result ?? 'not sent yet'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button disabled={!d.configured} onClick={() => run(() => api(`/api/report-emails/${s.id}/send-now`, { body: {} }), 'Sent. Check the inbox.')}>Send now</button>{' '}
                  <button onClick={() => run(() => api(`/api/report-emails/${s.id}`, { method: 'PATCH', body: { active: !s.active } }))}>{s.active ? 'Pause' : 'Resume'}</button>{' '}
                  <button onClick={() => { if (confirm('Delete this email?')) run(() => api(`/api/report-emails/${s.id}`, { method: 'DELETE' })); }}>Delete</button>
                </td>
              </tr>))}</tbody></table>
          {d.schedules.length === 0 && <div className="empty">No report emails yet.</div>}
          <h3>Add one</h3>
          <div className="field-row">
            <label>Name<input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Morning sales" /></label>
            <label>Report<select value={f.report} onChange={(e) => setF({ ...f, report: e.target.value })}>{d.reports.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}</select></label>
            <label>How often<select value={f.frequency} onChange={(e) => setF({ ...f, frequency: e.target.value })}><option value="daily">Every day</option><option value="weekly">Every Monday</option><option value="monthly">The 1st of the month</option></select></label>
            <label>Send at<select value={f.sendHour} onChange={(e) => setF({ ...f, sendHour: e.target.value })}>{Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}</select></label>
          </div>
          <label>Send to (separate several addresses with commas)<input value={f.recipients} onChange={(e) => setF({ ...f, recipients: e.target.value })} placeholder="you@example.com, partner@example.com" /></label>
          <div><button className="primary" disabled={!f.name.trim() || !f.recipients.trim()} onClick={() => run(async () => {
            await api('/api/report-emails', { body: { name: f.name, report: f.report, frequency: f.frequency, sendHour: Number(f.sendHour), recipients: f.recipients.split(/[,;\s]+/).filter(Boolean) } });
            setF({ ...f, name: '', recipients: '' });
          }, 'Added.')}>Add report email</button></div>
        </>
      )}
    </div></div>
  );
}
